import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const openaiService = {
  async getFitnessCoaching(userMessage, sessionType = 'general', contextData = {}) {
    try {
      const systemPrompts = {
        workout_plan: 'You are an expert fitness coach specializing in personalized workout plans. Provide detailed, safe, and effective workout recommendations based on the user\'s goals, experience level, and available equipment.',
        nutrition_advice: 'You are a certified nutritionist specializing in sports nutrition and fitness. Provide evidence-based nutrition advice tailored to the user\'s fitness goals and dietary preferences.',
        form_check: 'You are an experienced personal trainer specializing in exercise form and injury prevention. Provide detailed feedback on exercise technique and safety.',
        motivation: 'You are a motivational fitness coach. Provide encouraging, positive, and actionable advice to help users stay committed to their fitness journey.',
        general: 'You are a knowledgeable fitness and wellness coach. Provide helpful, accurate, and personalized guidance on all aspects of fitness, nutrition, and healthy living.'
      };

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { 
            role: 'system', 
            content: systemPrompts?.[sessionType] || systemPrompts?.general
          },
          {
            role: 'user',
            content: `${userMessage}\n\nContext: ${JSON.stringify(contextData)}`
          }
        ],
        max_tokens: 1000,
        temperature: 0.7,
      });

      return {
        success: true,
        response: response?.choices?.[0]?.message?.content,
        usage: response?.usage
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error in AI coaching:', error);
      }
      return {
        success: false,
        error: errorInfo?.message
      };
    }
  },

  async getWorkoutSuggestions(userGoals, experienceLevel, availableEquipment) {
    const contextData = {
      goals: userGoals,
      experience: experienceLevel,
      equipment: availableEquipment
    };

    const message = `I need a workout plan. My goals are: ${userGoals?.join(', ')}. My experience level is ${experienceLevel}. Available equipment: ${availableEquipment?.join(', ')}.`;

    return this.getFitnessCoaching(message, 'workout_plan', contextData);
  },

  async getNutritionAdvice(userGoals, dietaryRestrictions, currentWeight, targetWeight) {
    const contextData = {
      goals: userGoals,
      restrictions: dietaryRestrictions,
      current_weight: currentWeight,
      target_weight: targetWeight
    };

    const message = `I need nutrition advice. Goals: ${userGoals?.join(', ')}. Dietary restrictions: ${dietaryRestrictions?.join(', ') || 'none'}. Current weight: ${currentWeight}kg, Target: ${targetWeight}kg.`;

    return this.getFitnessCoaching(message, 'nutrition_advice', contextData);
  },

  async getMotivationalMessage(userContext) {
    const message = `I need motivation. ${userContext}`;
    return this.getFitnessCoaching(message, 'motivation', { context: userContext });
  },

  async generateRevenueForecast(historicalDataSummary, statistics) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5',
        messages: [
          {
            role: 'system',
            content:
              'You are an expert financial analyst specializing in revenue forecasting for gym and fitness businesses. Analyze historical payment data and provide accurate revenue predictions with confidence intervals. Consider seasonality, trends, and business patterns typical in the fitness industry.',
          },
          {
            role: 'user',
            content: `Analyze this payment data and provide revenue forecasts:\n\n${historicalDataSummary}\n\nProvide forecasts for 7 days, 30 days, and 90 days with confidence levels, trend analysis, actionable insights, and recommendations for gym owners.`,
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'revenue_forecast_response',
            schema: {
              type: 'object',
              properties: {
                short_term: {
                  type: 'object',
                  properties: {
                    predicted_revenue: { type: 'number' },
                    confidence: { type: 'number' },
                    trend: { type: 'string' },
                  },
                  required: ['predicted_revenue', 'confidence', 'trend'],
                },
                medium_term: {
                  type: 'object',
                  properties: {
                    predicted_revenue: { type: 'number' },
                    confidence: { type: 'number' },
                    trend: { type: 'string' },
                  },
                  required: ['predicted_revenue', 'confidence', 'trend'],
                },
                long_term: {
                  type: 'object',
                  properties: {
                    predicted_revenue: { type: 'number' },
                    confidence: { type: 'number' },
                    trend: { type: 'string' },
                  },
                  required: ['predicted_revenue', 'confidence', 'trend'],
                },
                insights: {
                  type: 'array',
                  items: { type: 'string' },
                },
                recommendations: {
                  type: 'array',
                  items: { type: 'string' },
                },
              },
              required: ['short_term', 'medium_term', 'long_term', 'insights', 'recommendations'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'high',
        verbosity: 'medium',
      });

      const forecast = JSON.parse(response?.choices?.[0]?.message?.content);
      return {
        ...forecast,
        generated_at: new Date()?.toISOString(),
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating revenue forecast:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Analyze cash discrepancies and flag suspicious patterns
   */
  async analyzeCashDiscrepancy({
    variance,
    expectedBalance,
    actualBalance,
    transactions,
    drawerName,
    reconciledBy,
    shiftDuration,
    historicalVariances
  }) {
    try {
      // Build transaction summary
      const transactionSummary = transactions?.map(t => ({
        amount: t?.amount,
        type: t?.transaction_type,
        time: t?.created_at,
        payment_method: t?.payment_method
      })) || [];

      // Calculate transaction patterns
      const totalTransactions = transactions?.length || 0;
      const transactionAmounts = transactions?.map(t => parseFloat(t?.amount)) || [];
      const averageTransaction = transactionAmounts?.length > 0 
        ? transactionAmounts?.reduce((sum, amt) => sum + amt, 0) / transactionAmounts?.length 
        : 0;
      const largestTransaction = transactionAmounts?.length > 0 ? Math.max(...transactionAmounts) : 0;
      
      // Identify unusual transactions (3x average or more)
      const unusualTransactions = transactions?.filter(t => 
        parseFloat(t?.amount) > averageTransaction * 3
      ) || [];

      const contextData = {
        variance,
        expectedBalance,
        actualBalance,
        variancePercentage: expectedBalance > 0 ? ((variance / expectedBalance) * 100)?.toFixed(2) : 0,
        drawerName,
        reconciledBy,
        shiftDuration,
        totalTransactions,
        averageTransaction: averageTransaction?.toFixed(2),
        largestTransaction: largestTransaction?.toFixed(2),
        unusualTransactionCount: unusualTransactions?.length,
        historicalVariances: historicalVariances || []
      };

      const prompt = `You are a financial fraud detection expert specializing in cash handling analysis for retail and fitness businesses.

Analyze this cash reconciliation discrepancy:

**Variance Details:**
- Variance Amount: $${variance?.toFixed(2)} (${variance >= 0 ? 'Overage' : 'Shortage'})
- Expected Balance: $${expectedBalance?.toFixed(2)}
- Actual Balance: $${actualBalance?.toFixed(2)}
- Variance Percentage: ${contextData?.variancePercentage}%

**Transaction Context:**
- Total Transactions: ${totalTransactions}
- Average Transaction: $${contextData?.averageTransaction}
- Largest Transaction: $${contextData?.largestTransaction}
- Unusual Transactions (>3x average): ${unusualTransactions?.length}

**Operational Context:**
- Drawer: ${drawerName}
- Reconciled By: ${reconciledBy}
- Shift Duration: ${shiftDuration || 'Unknown'}
- Historical Variances: ${historicalVariances?.length || 0} previous incidents

**Recent Transactions Sample:**
${transactionSummary?.slice(0, 10)?.map((t, i) => `${i + 1}. $${t?.amount} - ${t?.type} - ${t?.payment_method} - ${new Date(t.time)?.toLocaleTimeString()}`)?.join('\n')}

Provide a comprehensive analysis in JSON format with:
1. **summary**: Brief explanation of the variance (2-3 sentences)
2. **suspiciousPatterns**: Array of specific suspicious patterns detected (if any)
3. **riskLevel**: "low", "medium", "high", or "critical"
4. **recommendations**: Array of 3-5 specific investigation steps
5. **possibleCauses**: Array of likely explanations for the discrepancy

Focus on:
- Transaction timing patterns
- Unusual transaction amounts or frequencies
- Cash handling errors vs intentional discrepancies
- Historical pattern comparison
- Staff behavior indicators`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are a financial fraud detection expert. Analyze cash discrepancies objectively and provide actionable insights. Always respond in valid JSON format.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        response_format: { type: "json_object" },
        max_tokens: 1500,
        temperature: 0.3, // Lower temperature for more consistent analysis
      });

      const analysisText = response?.choices?.[0]?.message?.content;
      const analysis = JSON.parse(analysisText);

      return {
        success: true,
        analysis: {
          summary: analysis?.summary || 'Analysis completed',
          suspiciousPatterns: analysis?.suspiciousPatterns || [],
          riskLevel: analysis?.riskLevel || 'medium',
          recommendations: analysis?.recommendations || [],
          possibleCauses: analysis?.possibleCauses || [],
          transactionPatterns: {
            totalTransactions,
            averageTransaction,
            largestTransaction,
            unusualCount: unusualTransactions?.length
          }
        },
        usage: response?.usage
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error analyzing cash discrepancy:', error);
      }
      return {
        success: false,
        error: errorInfo?.message,
        // Provide fallback analysis
        analysis: {
          summary: `Cash variance of $${Math.abs(variance)?.toFixed(2)} detected. Manual investigation required.`,
          suspiciousPatterns: [],
          riskLevel: Math.abs(variance) > 50 ? 'high' : Math.abs(variance) > 20 ? 'medium' : 'low',
          recommendations: [
            'Verify all cash denominations were counted correctly',
            'Review transaction logs for the shift period',
            'Interview staff member who performed reconciliation',
            'Check for voided or refunded transactions'
          ],
          possibleCauses: [
            'Cash counting error',
            'Unreported transaction',
            'Till shortage or overage'
          ],
          transactionPatterns: {
            totalTransactions: transactions?.length || 0,
            averageTransaction: 0,
            largestTransaction: 0,
            unusualCount: 0
          }
        }
      };
    }
  }
};

export default openaiService;
